import React, { forwardRef, PropsWithChildren, useState } from "react";
import useStore, { ClasswizeState } from "@/stores/index";
import { User, userDisplayText } from "@/js/utils/userUtils";
import { isChatFeatureEnabled } from "@/js/utils/chatUtils";
import SendMessageModal from "../SendMessageModal";
import { StudentTile } from "../StudentTile";
import { isPauseInternetEnabled } from "@/stores/classwizeConfigStore";
import { StudentTileHeader } from "../StudentTile/StudentTileHeader";
import { StudentTileContent } from "../StudentTile/StudentTileContent";
import { togglePauseForStudent } from "@/api/pause";
import { togglePauseForStudentInState } from "../RegularClassroom/utils";
import { StudentTileButton } from "./components/StudentTileButton";
import { useClassroomPolicyStore } from "@/stores/v2/classroomPolicyStore";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { isPlatformChatSupported } from "@/js/utils/chatUtils";
import { WindowData } from "@/js/components/RegularClassroomUser/utils";
import { ClassroomDetails } from "@/stores/classroomStore_I";
import { Flex, Spinner, Text, Tooltip } from "@chakra-ui/react";
import { colours } from "@/utils/themeUtils";
import { useTranslation } from "react-i18next";

interface RegularClassroomUserProps {
	classroom: ClassroomDetails;
	user: User;
	windowData: WindowData;
	deviceIndex: number;
	view: "tabs" | "screens";
	classIsActive: boolean;
	canCloseTab: boolean;
	showOpenTabBtn: boolean;
	showStudentAnnouncementBtn: boolean;
	tabItemLimit: number;
	studentsAreFocused: boolean;
	studentIsFocused: boolean;
	isUpdatingFocus: boolean;
	activeFocusIsLocked: boolean;
	studentIsBeingScreenshared: boolean;
	handleToggleFocusForStudent: (stableIdV1: string) => void;
	openNewTabModal: (user: User) => void;
	openP2pModal: (
		user: User,
		tabId: string | undefined,
		chromeId: string | undefined,
		windowId: string | undefined,
		isActiveTab: boolean
	) => void;
	classOpenTime?: number;
	/** When true, show the claim-management banner instead of chat / focus actions. */
	showStudentClaimBanner: boolean;
	/** When true, claim banner shows a spinner and ignores clicks (parent owns in-flight claim). */
	isStudentClaimBannerLoading?: boolean;
	/** Tooltip for the claim banner (parent-computed). */
	studentClaimBannerTooltip?: string;
	onStudentClaimBannerClick?: () => void;
	/** When true, chat actions are hidden (computed by RegularClassroom from store). */
	chatDisabled: boolean;
	isStudentClaimReleasable: boolean;
	onStudentClaimReleaseClick: () => void;
}

const RegularClassroomUser = forwardRef<HTMLDivElement, PropsWithChildren<RegularClassroomUserProps>>(
	(props: RegularClassroomUserProps, ref) => {
		const { t } = useTranslation();
		const [singleMessageModalOpen, setSingleMessageModalOpen] = useState(false);

		const classroomSourceId = useUserSessionStore((state) => state.getActiveGroup());
		const classroomIdForChat = props.classroom?.name;
		const threadKey = useStore((state: ClasswizeState) => state.chat.getThreadKey(props.classroom, props.user));

		const sendMessageModalTitle = React.useMemo(() => {
			return t("classroomDetails.sendMessageTo", { name: userDisplayText(props.user) });
		}, [props.user, t]);

		const isChatSupported = React.useMemo(() => {
			return isPlatformChatSupported(props.windowData);
		}, [props.windowData]);

		const handleTogglePause = () => {
			const teacherId = useStore.getState().classroom.getTeacherStableIdV1();
			const classroomId = props.classroom.stableIdV1;
			const studentId = props.user.username;

			togglePauseForStudent(teacherId, classroomId, studentId)
				.then(() => {
					togglePauseForStudentInState(props.user.username);
				})
				.catch(() => {
					console.error("Failed to toggle pause for student");
				});
		};

		const handleToggleFocus = () => {
			props.handleToggleFocusForStudent(props.user.stableIdV1);
		};

		const handleToggleSingleMessageClass = async () => {
			setSingleMessageModalOpen(!singleMessageModalOpen);
		};

		const handleSingleChat = (threadKey: string | undefined) => {
			// TODO refactor this logic block to immediately select classroom student and threadlist
			useStore.getState().chat.setSelectedClassId(classroomIdForChat ?? "");
			useStore.getState().chat.setSelectedStudentName(props.user.username);

			// set selected thread
			useStore.getState().chat.setSelectedThreadKey(threadKey ?? "");

			if (!useStore.getState().chat.chatWindowOpen) {
				useStore.getState().chat.toggleChatWindow(false);
			}
		};

		const isStudentPaused = () => {
			return useClassroomPolicyStore.getState().getPausedStudents().indexOf(props.user.username) >= 0;
		};

		const studentDisplayName = userDisplayText(props.user);
		return (
			<StudentTile ref={ref} username={props.user.username}>
				<StudentTileHeader
					user={props.user}
					isStudentClaimReleasable={props.isStudentClaimReleasable}
					onStudentClaimReleaseClick={props.onStudentClaimReleaseClick}
					actionsJsx={
						<>
							{props.showStudentClaimBanner ? (
								<Tooltip label={props.studentClaimBannerTooltip ?? ""} placement="top" isDisabled={props.isStudentClaimBannerLoading}>
									<Flex
										as="button"
										type="button"
										alignItems="center"
										justifyContent="center"
										grow={1}
										width="100%"
										height="30px"
										px={2}
										borderRadius="md"
										bg={colours.background.secondary}
										color="white"
										cursor="pointer"
										border="1px solid transparent"
										onClick={() => {
											if (props.isStudentClaimBannerLoading) return;
											props.onStudentClaimBannerClick?.();
										}}
										disabled={props.isStudentClaimBannerLoading}
										_hover={props.isStudentClaimBannerLoading ? {} : { bg: colours.background.secondary, color: "white" }}
										_disabled={{ cursor: "not-allowed", opacity: 0.5 }}
										aria-busy={props.isStudentClaimBannerLoading ? true : undefined}
										aria-label={props.isStudentClaimBannerLoading ? t("classroomDetails.claimStudentLoadingAria") : undefined}
										data-testid="student-claim-btn"
										id={`student-claim-btn-${props.user.username}`}
									>
										{props.isStudentClaimBannerLoading ? (
											<Spinner aria-hidden color="inherit" width="12px" height="12px" />
										) : (
											<Text as="span" fontSize="sm" fontWeight={500} lineHeight="30px">
												{t("classroomDetails.claimStudent")}
											</Text>
										)}
									</Flex>
								</Tooltip>
							) : (
								<>
									{isChatFeatureEnabled() && !props.chatDisabled && (
										<StudentTileButton
											tooltipText={
												!props.classIsActive
													? ""
													: !isChatSupported
														? t("classroomDetails.chatNotSupported")
														: t("classroomDetails.startChatWith", { name: userDisplayText(props.user) })
											}
											icon="fa-message-lines"
											handleClick={() => handleSingleChat(threadKey)}
											disabled={!props.classIsActive || !isChatSupported}
											buttonTestId="student-chat-btn"
										/>
									)}
									{props.showStudentAnnouncementBtn && (
										<StudentTileButton
											tooltipText={!props.classIsActive ? "" : t("classroomDetails.sendMessageToStudent", { name: studentDisplayName })}
											icon="fa-bullhorn"
											handleClick={handleToggleSingleMessageClass}
											disabled={!props.classIsActive}
											buttonTestId="student-announcement-btn"
										/>
									)}
									{props.showOpenTabBtn && (
										<StudentTileButton
											tooltipText={!props.classIsActive ? "" : t("classroomDetails.openTabFor", { name: studentDisplayName })}
											icon="fa-browser"
											handleClick={() => props.openNewTabModal(props.user)}
											disabled={!props.classIsActive || props.studentIsFocused}
											buttonTestId="student-opentab-btn"
										/>
									)}
									{isPauseInternetEnabled() && (
										<StudentTileButton
											tooltipText={
												!props.classIsActive
													? ""
													: isStudentPaused()
														? t("classroomDetails.resumeInternetFor", { name: studentDisplayName })
														: t("classroomDetails.pauseInternetFor", { name: studentDisplayName })
											}
											icon="fa-pause"
											handleClick={handleTogglePause}
											disabled={!props.classIsActive}
											buttonTestId={"pause-student-btn"}
											isActive={isStudentPaused()}
											activeVariant="red-outline"
										/>
									)}
									{props.studentsAreFocused && (
										<StudentTileButton
											tooltipText={
												props.studentIsFocused
													? t("classroomDetails.endFocusFor", { name: studentDisplayName })
													: !props.classIsActive
														? ""
														: props.activeFocusIsLocked && props.studentIsBeingScreenshared
															? t("classroomDetails.cannotAddToLockedFocusWhileScreensharing", {
																	name: studentDisplayName,
																})
															: t("classroomDetails.addToActiveFocus", { name: studentDisplayName })
											}
											icon="fa-arrows-to-eye"
											handleClick={handleToggleFocus}
											disabled={
												!props.classIsActive || (!props.studentIsFocused && props.activeFocusIsLocked && props.studentIsBeingScreenshared)
											}
											buttonTestId={"focus-student-btn"}
											isActive={props.studentIsFocused}
											activeVariant="blue-solid"
											actionIsLoading={props.isUpdatingFocus}
										/>
									)}
								</>
							)}
						</>
					}
				/>
				<StudentTileContent
					user={props.user}
					view={props.view}
					canCloseTab={props.canCloseTab}
					isClassActive={props.classIsActive}
					windowData={props.windowData}
					classroom={props.classroom}
					openP2pModal={props.openP2pModal}
					tabItemLimit={props.tabItemLimit}
					classOpenTime={props.classOpenTime}
					isStudentClaimed={!props.showStudentClaimBanner}
				/>

				<SendMessageModal
					open={singleMessageModalOpen}
					title={sendMessageModalTitle}
					classroomSourceId={classroomSourceId}
					users={[props.user]}
					onClose={() => setSingleMessageModalOpen(false)}
					subtitle={t("classroomDetails.studentMessageSubtitle")}
				/>
			</StudentTile>
		);
	}
);

export default RegularClassroomUser;
